import { useState } from 'react';
import { Link } from '@inertiajs/react';
import { cn } from '../lib/cn';
import { InputLabel, InputGroup } from './ui';

export default function BookPicker({ books, selectedIds, onChange }) {
  const [filter, setFilter] = useState('');

  const filtered = filter
    ? books.filter(b =>
        b.title.toLowerCase().includes(filter.toLowerCase()) ||
        (b.author && b.author.toLowerCase().includes(filter.toLowerCase()))
      )
    : books;

  const toggle = (bookId) => {
    if (selectedIds.includes(bookId)) {
      onChange(selectedIds.filter(id => id !== bookId));
    } else {
      onChange([...selectedIds, bookId]);
    }
  };

  if (books.length === 0) {
    return (
      <InputGroup>
        <InputLabel>Books</InputLabel>
        <div className="p-4 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">No books in your library yet.</p>
          <Link
            href="/teacher/library"
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline mt-1 inline-block"
          >
            Go to Library
          </Link>
        </div>
      </InputGroup>
    );
  }

  return (
    <InputGroup>
      <InputLabel>
        Books{selectedIds.length > 0 && (
          <span className="ml-1.5 text-xs font-normal text-gray-500 dark:text-gray-400">
            ({selectedIds.length} selected)
          </span>
        )}
      </InputLabel>

      {books.length > 5 && (
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter books..."
          className="w-full mb-2 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-400"
        />
      )}

      <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
        {filtered.map((book) => {
          const isSelected = selectedIds.includes(book.id);
          return (
            <label
              key={book.id}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors',
                isSelected
                  ? 'bg-blue-50 dark:bg-blue-900/20'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
              )}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggle(book.id)}
                className="rounded border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-gray-400"
              />
              {book.cover_image_url ? (
                <img
                  src={book.cover_image_url}
                  alt=""
                  className="w-8 h-11 object-cover rounded flex-shrink-0"
                />
              ) : (
                <div className="w-8 h-11 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{book.title}</p>
                {book.author && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{book.author}</p>
                )}
              </div>
            </label>
          );
        })}
        {filtered.length === 0 && (
          <p className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
            No books match your filter.
          </p>
        )}
      </div>
    </InputGroup>
  );
}
